/**
 * <dv-typing> — escribe su propio texto carácter por carácter la primera vez
 * que entra en pantalla.
 *
 * El texto vive en el HTML, así que si el JS no carga (o el usuario prefiere
 * menos movimiento) simplemente se ve completo. Nunca queda vacío.
 *
 * Atributos:
 *   speed — milisegundos por carácter (default 45)
 */
class DvTyping extends HTMLElement {
  #observer = null;

  connectedCallback() {
    const text = this.textContent.trim();
    if (!text) return;

    // En el editor de temas y con reduced-motion mostramos el texto completo:
    // animarlo estorbaría para editar y no aporta nada.
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const designMode = window.Shopify?.designMode;
    if (reducedMotion || designMode) return;

    this.textContent = '';

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#disconnectObserver();
          this.#type(text);
        }
      },
      { rootMargin: '0px 0px -15% 0px' }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#disconnectObserver();
  }

  #disconnectObserver() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #type(text) {
    const speed = Number(this.getAttribute('speed')) || 45;
    let index = 0;

    const step = () => {
      // El elemento pudo haberse quitado del DOM (cambio de sección en el editor).
      if (!this.isConnected) return;

      index += 1;
      this.textContent = text.slice(0, index);

      if (index < text.length) {
        setTimeout(step, speed);
      } else {
        this.setAttribute('data-typed', '');
      }
    };

    step();
  }
}

if (!customElements.get('dv-typing')) {
  customElements.define('dv-typing', DvTyping);
}

/**
 * <dv-rotator> — va mostrando sus slides de uno en uno, en ciclo.
 *
 * El primer slide ya viene marcado con .is-active desde Liquid, así que sin
 * JavaScript se ve una ficha fija en vez de nada. Los slides ocultos llevan
 * `inert`, de modo que nunca son alcanzables con teclado aunque estén en el DOM.
 *
 * Se detiene al pasar el mouse, al enfocar algo dentro, cuando la pestaña
 * pasa a segundo plano, y si el visitante pidió menos movimiento.
 *
 * Atributos:
 *   interval — milisegundos entre slides (default 5000)
 */
class DvRotator extends HTMLElement {
  #slides = [];
  #index = 0;
  #timer = null;

  connectedCallback() {
    this.#slides = Array.from(this.querySelectorAll('[data-slide]'));

    // Con un solo drop no hay nada que rotar. En el editor de temas tampoco:
    // el movimiento estorba mientras se acomodan los ajustes.
    if (this.#slides.length < 2) return;
    if (window.Shopify?.designMode) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.addEventListener('pointerenter', this.#pause);
    this.addEventListener('pointerleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);

    this.#resume();
  }

  disconnectedCallback() {
    this.#pause();
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
  }

  #onVisibilityChange = () => {
    if (document.hidden) this.#pause();
    else this.#resume();
  };

  #pause = () => {
    if (this.#timer === null) return;
    clearInterval(this.#timer);
    this.#timer = null;
  };

  #resume = () => {
    if (this.#timer !== null || document.hidden) return;
    const interval = Number(this.getAttribute('interval')) || 5000;
    this.#timer = setInterval(this.#next, interval);
  };

  #next = () => {
    this.#show((this.#index + 1) % this.#slides.length);
  };

  #show(next) {
    this.#index = next;

    this.#slides.forEach((slide, position) => {
      const active = position === next;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', String(!active));
    });
  }
}

if (!customElements.get('dv-rotator')) {
  customElements.define('dv-rotator', DvRotator);
}
